import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
} from "../../../../components";
import { Divider, Skeleton, Tooltip } from "@mui/material";
import { MonitorFinanceiroService } from "../../../../services";
import { responseErros } from "../../../../utils";

const monitorFinanceiroService = new MonitorFinanceiroService();

const defaultData = {
  pme: 0,
  pmr: 0,
  pmp: 0,
  cicloOperacional: 0,
  cicloFinanceiro: 0,
  estoqueMedio: 0,
  cmv: 0,
  temEstoque: false,
  estoqueSemControle: false,
};

const COR_PME = "#1976d2";
const COR_PMR = "#16a34a";
const COR_PMP = "#d32f2f";
const COR_ECONOMICO = "#16a34a";
const COR_FINANCEIRO = "#1976d2";

function PrazoCard({ titulo, dias, cor, icone, descricao, aviso }) {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #eef0f3",
        borderRadius: 2,
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
        p: 1.5,
        textAlign: "center",
        height: "100%",
        position: "relative",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}
      >
        {titulo}
        {aviso && (
          <Tooltip arrow title={aviso} placement="right">
            <Chip
              label={
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <i
                    className="ph-fill ph-warning"
                    style={{ color: "#b45309", fontSize: 11 }}
                  />
                  <Typography
                    sx={{ color: "#b45309", fontSize: 9.5, fontWeight: 700 }}
                  >
                    apróx.
                  </Typography>
                </div>
              }
              size="small"
              sx={{
                ml: 1,
                height: 16,
                fontSize: 9,
                fontWeight: 700,
                color: "#b45309",
                backgroundColor: "#f59e0b22",
                cursor: "help",
              }}
            />{" "}
          </Tooltip>
        )}
      </Typography>
      <Typography
        sx={{ fontWeight: 800, color: cor, fontSize: 30, lineHeight: 1.2 }}
      >
        {dias}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "text.disabled", ml: 0.5 }}
        >
          dias
        </Typography>
      </Typography>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          backgroundColor: `${cor}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mt: 0.5,
        }}
      >
        <i
          className={`ph-bold ${icone}`}
          style={{ color: cor, fontSize: 20 }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.disabled",
          display: "block",
          mt: 0.5,
          lineHeight: 1.2,
        }}
      >
        {descricao}
      </Typography>
    </Box>
  );
}

function ResumoCard({ titulo, dias, formula, cor }) {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${cor}33`,
        borderLeft: `4px solid ${cor}`,
        borderRadius: 2,
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        {titulo}
      </Typography>
      <Typography
        sx={{ fontWeight: 800, color: cor, fontSize: 34, lineHeight: 1.1 }}
      >
        {dias}
        <Typography
          component="span"
          variant="body2"
          sx={{ color: cor, ml: 0.5, fontWeight: 600 }}
        >
          dias
        </Typography>
      </Typography>
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {formula}
      </Typography>
    </Box>
  );
}

function MonitorFinCiclo({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody]);

  async function buscar() {
    setLoading(true);
    const result = await monitorFinanceiroService.getDashboardCiclo(
      filtrosBody,
    );
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const {
    pme,
    pmr,
    pmp,
    cicloOperacional,
    cicloFinanceiro,
    temEstoque,
    estoqueSemControle,
  } = data;
  const semDados = !pme && !pmr && !pmp;

  const fases = [
    {
      key: "pme",
      titulo: "Prazo Médio de Estocagem",
      dias: pme,
      cor: COR_PME,
      icone: "ph-package",
      descricao: temEstoque
        ? "produto parado até vender"
        : "sem controle de estoque",
      evento: "Aquisição de Mercadoria",
      aviso: estoqueSemControle
        ? "Esta unidade permite venda com estoque negativo (sem controle rígido de estoque). O PME é aproximado: consideramos apenas produtos com saldo ≥ 0."
        : null,
    },
    {
      key: "pmr",
      titulo: "Prazo Médio de Recebimento",
      dias: pmr,
      cor: COR_PMR,
      icone: "ph-hand-coins",
      descricao: "da venda até receber",
      evento: "Venda / Prestação de Serviço",
    },
    {
      key: "pmp",
      titulo: "Prazo Médio de Pagamento",
      dias: pmp,
      cor: COR_PMP,
      icone: "ph-credit-card",
      descricao: "da compra até pagar",
      evento: "Pagamento a Fornecedores",
    },
  ];

  const escalaMax = Math.max(50, cicloOperacional, pmp);
  const ticks = Array.from({ length: 6 }, (_, i) =>
    Math.round((escalaMax * i) / 5),
  );

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <i
              className="ph-bold ph-arrows-clockwise"
              style={{ color: "#64748b", fontSize: 20 }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Ciclo Operacional e Financeiro · Últimos 12 meses
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tempo entre comprar, vender, receber e pagar
              </Typography>
            </Box>
          </Box>
          <Tooltip
            arrow
            title="Ciclo Econômico = PME + PMR (do estoque ao recebimento). Ciclo Financeiro = Ciclo Econômico − PMP (dias bancados com caixa próprio)."
            placement="left"
          >
            <i
              className="ph-bold ph-info"
              style={{ color: "#94a3b8", fontSize: 18, cursor: "help" }}
            />
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Skeleton
            variant="rectangular"
            height={260}
            sx={{ borderRadius: 1 }}
          />
        ) : semDados ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography variant="body2" color="text.secondary">
              Sem dados suficientes para calcular o ciclo no período.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} lg={8}>
              <Grid container spacing={1.5}>
                {fases.map((f) => (
                  <Grid item xs={12} sm={4} key={f.key}>
                    <PrazoCard
                      titulo={f.titulo}
                      dias={f.dias}
                      cor={f.cor}
                      icone={f.icone}
                      descricao={f.descricao}
                      aviso={f.aviso}
                    />
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: { xs: "none", sm: "block" }, mt: 2 }}>
                <Box sx={{ display: "flex" }}>
                  {fases.map((f, i) => (
                    <Box
                      key={f.key}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            borderTop: "2px dashed #cbd5e1",
                            visibility: i === 0 ? "hidden" : "visible",
                          }}
                        />
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: f.cor,
                            boxShadow: `0 0 0 3px ${f.cor}22`,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            borderTop: "2px dashed #cbd5e1",
                            visibility:
                              i === fases.length - 1 ? "hidden" : "visible",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          color: "text.secondary",
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {f.evento}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 1.5,
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 0.5,
                  }}
                >
                  {ticks.map((t) => (
                    <Typography
                      key={t}
                      variant="caption"
                      sx={{ color: "text.disabled", fontSize: 10 }}
                    >
                      {t}
                    </Typography>
                  ))}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.disabled",
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    Dias
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <ResumoCard
                  titulo="Ciclo Econômico"
                  dias={cicloOperacional}
                  formula="PME + PMR"
                  cor={COR_ECONOMICO}
                />
                <ResumoCard
                  titulo="Ciclo Financeiro"
                  dias={cicloFinanceiro}
                  formula="Ciclo Econômico − PMP"
                  cor={COR_FINANCEIRO}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorFinCiclo;
